import { Button } from '@mui/material';
import { FunctionComponent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { PoGrid } from '@/components/grid/PoGrid';
import { AssetTypeModel } from '../../types/AssetTypeModel';
import { generateAssetTypeColumns } from '../../components/asset-type-grid.columns';
import { assetTypeService } from '../../api/asset-types/asset-type.service';
import { useApiCall } from '@/hooks/useApiCall';
import { GridOptions, RowDoubleClickedEvent } from 'ag-grid-community';
import ErrorLoadingDataAlert from '@/components/feedback/ErrorLoadingDataAlert';
import { TabbedLayout } from '@/modules/application/layouts/TabbedLayout';
import { TabbedLayoutTopBar } from '@/modules/application/components/TabbedLayoutTopBar';
import TabbedLayoutTopBarHeader from '@/modules/application/components/TabbedLayoutTopBarHeader';
import { TabbedPageLayoutBody } from '@/modules/application/components/TabbedPageLayoutBody';
import { AddOutlined } from '@mui/icons-material';
import RequirePermissionComponent from '@/components/permissions/RequirePermissionComponent';
import { Permission } from '@/modules/users/submodules/roles/api/permissions.contracts';
import { useTranslation } from '@/lib';
import FilterBar from '@/components/filterbar/FilterBar';
import TextFilter from '@/components/filterbar/filters/TextFilter';
import useQueryParamsFilters from '@/hooks/useQueryParamFilters';
import { CacheKey } from '@/providers/cache-provider/cache-key.enum';
import { FilterValues } from '@/components/filterbar/FilterBarContext';

export const AssetTypesPage: FunctionComponent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const { filters, setFiltersToUrl } = useQueryParamsFilters(
    {
      searchText: searchParams.get('searchText') ?? '',
    },
    CacheKey.ASSET_TYPE_FILTERS,
  );

  const { data, isLoading, isError, setApiCallArg } = useApiCall<AssetTypeModel[]>(() =>
    assetTypeService.getAll({ query: filters.current.searchText ?? '' }),
  );

  const handleSearch = (filterValues: FilterValues) => {
    setFiltersToUrl(filterValues);
    setApiCallArg(() => assetTypeService.getAll({ query: filterValues.searchText ?? '' }));
  };

  const customGridOptions: GridOptions<AssetTypeModel> = {
    onRowDoubleClicked(event: RowDoubleClickedEvent<AssetTypeModel, unknown>) {
      navigate(`./${event.data?.id}`);
    },
  };

  return (
    <TabbedLayout
      topBar={
        <TabbedLayoutTopBar
          actions={
            <RequirePermissionComponent permission={Permission.ASSET_TYPES_EDIT}>
              <Button startIcon={<AddOutlined></AddOutlined>} onClick={() => navigate('./create')}>
                {t('add')}
              </Button>
            </RequirePermissionComponent>
          }
        >
          <TabbedLayoutTopBarHeader icon={null}>{t('asset_types')}</TabbedLayoutTopBarHeader>
        </TabbedLayoutTopBar>
      }
    >
      <TabbedPageLayoutBody>
        <div className="flex h-full grow flex-col">
          {isError ? (
            <ErrorLoadingDataAlert />
          ) : (
            <>
              <div className="mb-2">
                <FilterBar onSearch={handleSearch} initialFilterValues={filters.current} isLoading={isLoading}>
                  <div className="w-44">
                    <TextFilter label={t('search')} />
                  </div>
                </FilterBar>
              </div>
              <PoGrid isLoading={isLoading} colDefs={generateAssetTypeColumns()} rowData={data} gridOptions={customGridOptions} />
            </>
          )}
        </div>
      </TabbedPageLayoutBody>
    </TabbedLayout>
  );
};
